import { ReportType } from '@/app/constants/reportType'
import moment from 'moment'
import React from 'react'
import usePrintService from '../services/PrintService'
import { useTask } from '@/modules/shared/context/TaskContext'
import { toast } from '@/lib/toasts/api'
import { TaskType } from '@/app/constants/taskType'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { useTimetable } from '@/modules/timetable/stores/timetableStore'
import { usePrintJobGuard } from '@/modules/printouts/hooks/usePrintJobGuard'

const usePrint = ({ companyId }: { companyId: string }) => {
  const { upcoming, onlyVisible, filteredTypes, selectedAlerts, dateRange } =
    useTimetable()
  const printService = usePrintService()
  const { addTask } = useTask()
  const { t } = useLocalize()
  const { isGenerating, busyTooltip, runPrintJob } = usePrintJobGuard()

  const dateFrom = React.useMemo(() => {
    const s = dateRange?.startDate
    if (s?.year != null && s?.month != null && s?.day != null) {
      return moment({ year: s.year, month: s.month - 1, day: s.day }).startOf(
        'day',
      )
    }
    return moment().subtract(30, 'days')
  }, [
    dateRange?.startDate?.year,
    dateRange?.startDate?.month,
    dateRange?.startDate?.day,
  ])

  const dateTo = React.useMemo(() => {
    const e = dateRange?.endDate
    if (e?.year != null && e?.month != null && e?.day != null) {
      return moment({ year: e.year, month: e.month - 1, day: e.day }).endOf(
        'day',
      )
    }
    return moment()
  }, [
    dateRange?.endDate?.year,
    dateRange?.endDate?.month,
    dateRange?.endDate?.day,
  ])

  const printAlerts = async ({ selected }: { selected: boolean }) => {
    try {
      let taskId: string

      if (!selected) {
        taskId = await printService.getPdf({
          companyId,
          reportType: ReportType.ALL_ALERTS,
          drivers: [],
          startTime: dateFrom,
          endTime: dateTo,
          data: {
            upcoming,
            onlyVisible,
            isDriver: false,
            filteredAlerts: filteredTypes,
            separateFiles: false,
          },
        })
        addTask(taskId, TaskType.Printout, ReportType.ALL_ALERTS)
        toast.success({
          text: t('printout.start-generation-title'),
        })
      } else if (selected && selectedAlerts?.length > 0) {
        taskId = await printService.getPdf({
          companyId,
          reportType: ReportType.SELECTED_ALERTS,
          drivers: [],
          startTime: dateFrom,
          endTime: dateTo,
          data: {
            upcoming,
            onlyVisible,
            isDriver: false,
            selectedAlerts: selectedAlerts,
            filteredAlerts: filteredTypes,
            separateFiles: false,
          },
        })
        addTask(taskId, TaskType.Printout, ReportType.SELECTED_ALERTS)
        toast.success({
          text: t('printout.start-generation-title'),
        })
      }
    } catch (error) {
      toast.error({
        text:
          t('printout.generation-error'),
      })
    }
  }

  const handlePrintConfirm = (params: { selected: boolean }) =>
    runPrintJob(() => printAlerts(params))

  return { handlePrintConfirm, isGenerating, busyTooltip }
}

export default usePrint
